import { useT } from "@transifex/react";
import type { CatalogBook } from "../../../helpers/BookCatalog";
import BookGrid from "./BookGrid";
import BookSelectionStepHeading from "./BookSelectionStepHeading";
import type { IsbnLookupResult } from "./useBookSelectionSteps";

interface BookSearchResultsProps {
    query: string;
    /** The state's approval hint, when the state filters the results. */
    approvalHint?: string;
    books: Array<CatalogBook>;
    /** Set when the query is a whole ISBN that didn't lead straight to its volume. */
    isbnLookup?: Exclude<IsbnLookupResult, "picked">;
    selectedBookId?: string;
    onPick: (book: CatalogBook) => void;
}

/** Shown in place of the current step while the teacher searches. */
const BookSearchResults = (props: BookSearchResultsProps) => {
    const t = useT();
    const query = props.query.trim();
    const t_title = t("Results for “{query}”", { _tags: "CreateTest,SelectBook", query });
    const t_noResults = t("No textbook found for “{query}”.", { _tags: "CreateTest,SelectBook", query });
    const t_isbnNotFound = t("No volume found for this ISBN.", { _tags: "CreateTest,SelectBook" });
    const t_bookNotAvailable = t("This book is not available for this activity.", {
        _tags: "CreateTest,SelectBook",
    });

    const getEmptyMessage = () => {
        if (props.isbnLookup === "unavailable") {
            return t_bookNotAvailable;
        }
        return props.isbnLookup === "not-found" ? t_isbnNotFound : t_noResults;
    };

    return (
        <div
            className="flex flex-col gap-s"
            data-testid="book-selection-search-results"
        >
            <BookSelectionStepHeading
                title={t_title}
                hint={props.approvalHint}
            />
            {props.books.length > 0 ? (
                <BookGrid
                    // A new search starts collapsed again.
                    key={query}
                    books={props.books}
                    selectedBookId={props.selectedBookId}
                    isLanguageShown
                    onPick={props.onPick}
                />
            ) : (
                <p
                    role="status"
                    className="m-no py-m text-m text-p6-grey-dark"
                >
                    {getEmptyMessage()}
                </p>
            )}
        </div>
    );
};

export default BookSearchResults;
